import React, { useContext } from "react";
import { useMediaQuery } from "@mui/material";
import { PatientHeaderDescription } from "../patientGroupHeader/PatientHeaderDescription";
import { HeaderActionComponent } from "../patientGroupHeader/HeaderActionComponent";
import {
  GROUP_TYPE,
  IGroupInfo,
  IMessageListFilter,
} from "@home-health-notify/domain";
import { GroupHeaderWrapper, LoadingOverlay } from "./GroupHeader.style";
import { GroupMessageSearch } from "./groupMessageSearch/GroupMessageSearch";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import { RESPONSIVE_DRAWER } from "../../../common/Common.style";
import { HeaderDescriptionWrapper } from "../../MessageHeader.style";
import { CommonChatHeaderDescription } from "../CommonChatHeaderDescription";
import { useSnackbar } from "notistack";
import { useTranslation } from "hhn-commons";

interface GroupHeaderProps {
  groupInfo: IGroupInfo;
  showSearch: boolean;
  setShowSearch: (toShow: boolean) => void;
  onFilter: (filters: IMessageListFilter) => void;
  isMemberFlag: boolean;
  isChatLoading: boolean;
}

export const GroupHeader = ({
  groupInfo,
  showSearch,
  setShowSearch,
  onFilter,
  isMemberFlag,
  isChatLoading,
}: GroupHeaderProps): React.JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const {
    openChatInfoDrawer,
    closeDrawer,
    open: chatInfoDrawerOpen,
  } = useContext(RightDrawerContext);

  const isNarrow = useMediaQuery(
    `(max-width: ${RESPONSIVE_DRAWER.WIDE_BP - 1}px)`,
  );
  const isCompactHeader = chatInfoDrawerOpen || isNarrow;

  const showLoadingSnackbar = () => {
    enqueueSnackbar(tNs("chatInfoLoading"), {
      autoHideDuration: 3000,
    });
  };

  const toggleChatInfoDrawer = () => {
    !chatInfoDrawerOpen ? openChatInfoDrawer() : closeDrawer();
  };

  const onChatInfoClick = () => {
    if (isChatLoading) return;
    toggleChatInfoDrawer();
  };

  const onMessageSearch = () => {
    if (isChatLoading) return;
    setShowSearch(true);
  };

  return (
    <GroupHeaderWrapper>
      <LoadingOverlay
        isLoading={isChatLoading}
        onClick={showLoadingSnackbar}
        data-testid="loading-overlay"
      />
      {!(showSearch && isCompactHeader) && (
        <HeaderDescriptionWrapper onClick={onChatInfoClick}>
          {groupInfo.groupType === GROUP_TYPE.PATIENT ? (
            <PatientHeaderDescription
              name={groupInfo.name}
              mrn={groupInfo.mrn}
              patientStatus={groupInfo.patientStatus}
              servicesToPatient={groupInfo.servicesToPatient}
              meta={groupInfo?.meta}
            />
          ) : (
            <CommonChatHeaderDescription chatInfo={groupInfo} />
          )}
        </HeaderDescriptionWrapper>
      )}
      {showSearch ? (
        <GroupMessageSearch
          groupInfo={groupInfo}
          onClearSearch={() => setShowSearch(false)}
          onFilter={onFilter}
          fullWidth={showSearch && isCompactHeader}
        />
      ) : (
        <HeaderActionComponent
          groupInfo={groupInfo}
          onMessageSearch={onMessageSearch}
          isMemberFlag={isMemberFlag}
          openChatInfo={onChatInfoClick}
          isChatLoading={isChatLoading}
        />
      )}
    </GroupHeaderWrapper>
  );
};
